import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export default function BlogPage() {
  const [blogs, setBlogs] = useState([]);
  const [paginationLinks, setPaginationLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  
  // استخدام i18n المدمج لإدارة اللغات والترجمة بشكل تلقائي
  const { t, i18n } = useTranslation();

  // جلب اللغة الحالية من i18next مباشرة أو كملاذ أخير من الـ localStorage
  const currentLang = i18n.language || localStorage.getItem('lang') || 'ar';

  // 1. دالة جلب البيانات من الـ API
  const fetchBlogs = async (url = 'https://ba7ary.draconiccode.com/public/api/blogs') => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(url);
      if (!response.ok) {
        // الاعتماد على i18n للترجمة الفورية للخطأ
        throw new Error(t('error_message', { defaultValue: 'حدث خطأ أثناء تحميل البيانات' }));
      }
      const result = await response.json();
      setBlogs(result.data || []);
      setPaginationLinks(result.meta?.links || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 2. إعادة جلب البيانات فور تغير لغة الموقع
  useEffect(() => {
    fetchBlogs();
  }, [currentLang]);

  // إعدادات اتجاه الصفحة ومحاذاة النصوص بناءً على اللغة الحالية
  const isRtl = currentLang === 'ar';
  const pageDirection = isRtl ? 'rtl' : 'ltr';
  const textAlignment = isRtl ? 'text-right' : 'text-left';

  // 3. دالة استخراج النص من الفقرة الأولى كـ Snippet
  const getSnippet = (blog) => {
    if (blog.paragraphs && blog.paragraphs.length > 0) {
      const firstParagraph = blog.paragraphs[0];
      return firstParagraph.content[currentLang] || firstParagraph.content['ar'] || '';
    }
    return '';
  };

  return (
    <div className={`bg-slate-50 min-h-screen ${textAlignment} transition-all duration-300`} dir={pageDirection}>
      
      {/* هيدر الصفحة الرئيسي */}
      <section className="bg-gradient-to-br from-slate-900 to-slate-800 text-white py-16 md:py-24 relative overflow-hidden shadow-inner h-[90vh] flex items-center justify-center">
        <div className="absolute inset-0 opacity-50 bg-[radial-gradient(#fff_1px,transparent_1px)] bg-cover bg-center" style={{ backgroundImage: 'url(/assets/blogs-bg.png)' }}></div>
        <div className="px-4 text-center relative z-10 h-fit">
          <h1 className="text-3xl md:text-5xl font-extrabold mb-4 leading-tight tracking-wide">
            {t("meta_title")}
          </h1>
          <p className="text-gray-300 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
            {t("meta_description")}
          </p>
        </div>
      </section>

      {/* المحتوى والمقالات */}
      <main className="max-w-7xl mx-auto px-4 py-12 flex flex-col gap-8">

        {/* حالة التحميل (Loading) */}
        {loading && (
          <div className="text-center py-12 text-slate-600 font-medium">
            {t("loading")}
          </div>
        )}

        {/* حالة الخطأ (Error) */}
        {error && (
          <div className="text-center py-12 text-red-600 font-medium">
            {error}
          </div>
        )}

        {/* عرض كروت المقالات الديناميكية */}
        {!loading && !error && (
          <div className="flex flex-wrap gap-6 justify-center md:justify-start">
            {blogs.length > 0 ? (
              blogs.map((blog) => (
                <div 
                  key={blog.id} 
                  className="w-full md:w-[calc(50%-12px)] bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden flex flex-col justify-between group max-w-[400px]"
                >
                  <div>
                    {/* بانر المقال */}
                    <div className="h-48 bg-slate-200 relative overflow-hidden">
                      {blog.banner_image ? (
                        <img 
                          src={blog.banner_image} 
                          alt={blog.title[currentLang] || blog.title['ar']} 
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-slate-700 to-slate-800"></div>
                      )}
                    </div>
                    
                    {/* تفاصيل المقال */}
                    <div className="p-5">
                      <h3 className="font-bold text-slate-800 text-lg mb-3 line-clamp-2 group-hover:text-blue-900 transition-colors">
                        {blog.title[currentLang] || blog.title['ar']}
                      </h3>
                      <p className="text-sm text-gray-500 leading-relaxed line-clamp-2">
                        {getSnippet(blog)}
                      </p>
                    </div>
                  </div>

                  {/* زر القراءة */}
                  <div className="p-5 pt-0">
                    <button className="w-full py-2.5 border border-gray-200 hover:border-blue-900 hover:bg-blue-50/30 text-xs font-semibold rounded-lg text-slate-700 hover:text-blue-950 transition" onClick={() => navigate(`/blogs/${blog.id}`)}>
                      {t("read_article")}
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center w-full py-12 text-gray-500">
                {t("no_articles")}
              </div>
            )}
          </div>
        )}

        {/* أزرار الترقيم الصفحي (Pagination) */}
        {!loading && !error && paginationLinks.length > 3 && (
          <div className="flex justify-center items-center gap-2 mt-6" dir="ltr">
            {paginationLinks.map((link, index) => {
              let label = link.label;
              if (label.includes('Previous')) label = '«';
              if (label.includes('Next')) label = '»';

              return (
                <button
                  key={index}
                  disabled={!link.url}
                  onClick={() => link.url && fetchBlogs(link.url)}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg border text-sm font-medium transition
                    ${link.active 
                      ? 'bg-blue-900 text-white border-blue-900 shadow-sm' 
                      : link.url 
                        ? 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50' 
                        : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                    }`}
                  dangerouslySetInnerHTML={{ __html: label }}
                />
              );
            })}
          </div>
        )}

      </main>
    </div>
  );
}